/**
 * RD stock-branch ↔ item-master-branch correspondence.
 *
 * Source of truth: Vera-Lynne “DC Cross References” email (2026-09-16),
 * recorded on GEN-2525. The two alphabets are disjoint — letter equality
 * false-flags valid pairs (O stock vs C item-master). Do not use
 * {@link AS400_BRANCHES_BY_DIVISION} here; that list is stocking-only
 * (GEN-2350).
 *
 * Record space also has **I** with no stock partner. Leave it unpaired;
 * do not invent a partner. Other divisions are out of scope until they
 * get their own table.
 *
 * Reusable for catalog / export / stock aggregation (GEN-2397 / GEN-2338)
 * — this module is the handoff, not a wiring of those paths.
 */

export interface RdDcCrossReference {
  /** Rexel DC name as Vera listed it. */
  dc: string;
  /** `as_stock.sloc` letter. */
  stockLetter: string;
  /** Item-master / record-space letter (`as_item.iloc`). */
  itemMasterLetter: string;
}

/**
 * The six RD DCs. Weekly call-outs highlight O↔C, A↔K, M↔H, E↔F;
 * D↔B and V↔J complete the table.
 */
export const RD_DC_CROSS_REFERENCES: readonly RdDcCrossReference[] = [
  { dc: "RKDC Kingston", stockLetter: "K", itemMasterLetter: "A" },
  { dc: "RDDC Edmonton", stockLetter: "D", itemMasterLetter: "B" },
  { dc: "RODC Ontario", stockLetter: "O", itemMasterLetter: "C" },
  { dc: "REDC Edmonton", stockLetter: "E", itemMasterLetter: "F" },
  { dc: "RMDC Winnipeg", stockLetter: "M", itemMasterLetter: "H" },
  { dc: "RVDC Vancouver", stockLetter: "V", itemMasterLetter: "J" },
];

/** Item-master letters that exist in RD record space with no stock partner. */
export const RD_UNPAIRED_ITEM_MASTER_LETTERS: readonly string[] = ["I"];

export type RdLocationUnmatchedReason =
  | "unmappedStockLocation"
  | "unmappedItemMasterLocation"
  | "unpairedItemMasterLocation"
  | "stockItemLocationMismatch";

export type RdLocationMatch =
  | {
      status: "mapped";
      stockLetter: string;
      itemMasterLetter: string;
      dc: string;
    }
  | {
      status: "unmatched";
      stockLetter: string;
      itemMasterLetter: string;
      reason: RdLocationUnmatchedReason;
    };

const STOCK_TO_DC: ReadonlyMap<string, RdDcCrossReference> = new Map(
  RD_DC_CROSS_REFERENCES.map((row) => [row.stockLetter, row]),
);

const ITEM_TO_DC: ReadonlyMap<string, RdDcCrossReference> = new Map(
  RD_DC_CROSS_REFERENCES.map((row) => [row.itemMasterLetter, row]),
);

const UNPAIRED_ITEM_MASTER = new Set(RD_UNPAIRED_ITEM_MASTER_LETTERS);

/** Trim + uppercase a branch letter. Empty input stays empty. */
export function normalizeRdBranchLetter(value: string): string {
  return value.trim().toUpperCase();
}

export function rdDcForStockLetter(
  stockLetter: string,
): RdDcCrossReference | undefined {
  return STOCK_TO_DC.get(normalizeRdBranchLetter(stockLetter));
}

export function rdDcForItemMasterLetter(
  itemMasterLetter: string,
): RdDcCrossReference | undefined {
  return ITEM_TO_DC.get(normalizeRdBranchLetter(itemMasterLetter));
}

/** Item-master letter that corresponds to a stock letter, if any. */
export function itemMasterLetterForRdStock(
  stockLetter: string,
): string | undefined {
  return rdDcForStockLetter(stockLetter)?.itemMasterLetter;
}

/** Stock letter that corresponds to an item-master letter, if any. */
export function stockLetterForRdItemMaster(
  itemMasterLetter: string,
): string | undefined {
  return rdDcForItemMasterLetter(itemMasterLetter)?.stockLetter;
}

export function isUnpairedRdItemMasterLetter(letter: string): boolean {
  return UNPAIRED_ITEM_MASTER.has(normalizeRdBranchLetter(letter));
}

/**
 * True only when both letters are a Vera pair (O↔C, K↔A, …).
 * Direct equality is never enough for RD.
 */
export function rdStockCorrespondsToItemMaster(
  stockLetter: string,
  itemMasterLetter: string,
): boolean {
  const item = normalizeRdBranchLetter(itemMasterLetter);
  if (item === "") return false;
  return itemMasterLetterForRdStock(stockLetter) === item;
}

/**
 * Compare a stock location and/or an item-master location through the RD
 * map. A stock-only (or item-only) known letter is a mapped match — the
 * partner is filled in from the table so STOCK_V rows are not false
 * unmatched. Both empty → null.
 */
export function matchRdStockAndItemMasterLocations(args: {
  stockLetter?: string;
  itemMasterLetter?: string;
}): RdLocationMatch | null {
  const stockLetter = normalizeRdBranchLetter(args.stockLetter ?? "");
  const itemMasterLetter = normalizeRdBranchLetter(args.itemMasterLetter ?? "");
  if (stockLetter === "" && itemMasterLetter === "") return null;
  if (stockLetter !== "" && itemMasterLetter !== "") {
    return matchBothLetters(stockLetter, itemMasterLetter);
  }
  if (stockLetter !== "") return matchStockLetterOnly(stockLetter);
  return matchItemMasterLetterOnly(itemMasterLetter);
}

function matchBothLetters(
  stockLetter: string,
  itemMasterLetter: string,
): RdLocationMatch {
  const dc = rdDcForStockLetter(stockLetter);
  if (dc && dc.itemMasterLetter === itemMasterLetter) {
    return { status: "mapped", stockLetter, itemMasterLetter, dc: dc.dc };
  }
  return {
    status: "unmatched",
    stockLetter,
    itemMasterLetter,
    reason: unmatchedReasonForPair(stockLetter, itemMasterLetter),
  };
}

function matchStockLetterOnly(stockLetter: string): RdLocationMatch {
  const dc = rdDcForStockLetter(stockLetter);
  if (dc) {
    return {
      status: "mapped",
      stockLetter,
      itemMasterLetter: dc.itemMasterLetter,
      dc: dc.dc,
    };
  }
  return {
    status: "unmatched",
    stockLetter,
    itemMasterLetter: "",
    reason: "unmappedStockLocation",
  };
}

function matchItemMasterLetterOnly(itemMasterLetter: string): RdLocationMatch {
  const dc = rdDcForItemMasterLetter(itemMasterLetter);
  if (dc) {
    return {
      status: "mapped",
      stockLetter: dc.stockLetter,
      itemMasterLetter,
      dc: dc.dc,
    };
  }
  return {
    status: "unmatched",
    stockLetter: "",
    itemMasterLetter,
    reason: isUnpairedRdItemMasterLetter(itemMasterLetter)
      ? "unpairedItemMasterLocation"
      : "unmappedItemMasterLocation",
  };
}

function unmatchedReasonForPair(
  stockLetter: string,
  itemMasterLetter: string,
): RdLocationUnmatchedReason {
  if (!itemMasterLetterForRdStock(stockLetter)) {
    return "unmappedStockLocation";
  }
  if (isUnpairedRdItemMasterLetter(itemMasterLetter)) {
    return "unpairedItemMasterLocation";
  }
  if (!stockLetterForRdItemMaster(itemMasterLetter)) {
    return "unmappedItemMasterLocation";
  }
  return "stockItemLocationMismatch";
}
